import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LiveStreamPlayer from '../LiveStreamPlayer';
import { useCameraStream } from '../../contexts/NodeStreamContext';
import { getNodeCameras } from '../../services/api.service';

/**
 * Live stream preview for the cloud ONVIF config page. Streams over the shared,
 * session-level connection (no per-component WS/PC) so opening the config page
 * reuses the warm connection instead of re-running the full ICE/TURN handshake.
 * When collapsed, the camera is unsubscribed so the SFU stops sending its track.
 *
 * RBAC pre-check: the node's camera list is RBAC-filtered per the acting user, so
 * if this camera isn't in it the user has no access — show "access denied" up
 * front instead of spinning on "connecting" (a viewer opening a config deep-link
 * never gets a track for a camera outside their scope).
 */
export default function StreamPreview({ nodeId, cameraId }: { nodeId: string; cameraId: string }) {
  const [collapsed, setCollapsed] = useState(false);
  // null = access not yet resolved; true = denied; false = allowed.
  const [accessDenied, setAccessDenied] = useState<boolean | null>(null);

  useEffect(() => {
    if (!nodeId || !cameraId) return;
    let cancelled = false;
    getNodeCameras(nodeId)
      .then((list: any) => {
        if (cancelled) return;
        const cams = Array.isArray(list) ? list : list?.data || list?.cameras || [];
        const has = cams.some((c: any) => (c.id || c.cameraId || c.camera_id) === cameraId);
        setAccessDenied(!has);
      })
      .catch(() => { if (!cancelled) setAccessDenied(false); }); // fail open — let the SFU signal decide
    return () => { cancelled = true; };
  }, [nodeId, cameraId]);

  const denied = accessDenied === true;
  const { stream, state, onStalled, onPlaying, onRetry } = useCameraStream(
    nodeId,
    cameraId,
    '',
    !collapsed && accessDenied === false, // subscribe only once access is confirmed
  );
  const effectiveState = denied ? 'access_denied' : state;

  return (
    <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted">
        <div className="flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full ${effectiveState === 'rtc_established' ? 'bg-emerald-500 animate-pulse' : denied || effectiveState === 'signaling_error' || effectiveState === 'network_error' || effectiveState === 'camera_unreachable' || effectiveState === 'node_unreachable' ? 'bg-red-500' : 'bg-amber-500 animate-pulse'}`} />
          <span className="text-[12px] font-medium text-foreground">Live Preview</span>
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          {collapsed ? <EyeIcon className="h-3.5 w-3.5" /> : <EyeSlashIcon className="h-3.5 w-3.5" />}
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {/* Video */}
      {!collapsed && (
        <div className="relative aspect-video bg-black">
          <LiveStreamPlayer
            stream={denied ? null : stream}
            connectionState={effectiveState}
            onStalled={onStalled}
            onPlaying={onPlaying}
            onRetry={onRetry}
            className="aspect-video"
          />
        </div>
      )}
    </div>
  );
}
